import uPlot from 'uplot';
import { peakLabel, rampMethodOf, ribbonLabel } from '../../constants/insightsText';
import {
  OBSERVED_STRIP, PIN_ROW_H, RIBBON_ROW, SLOT_SEC, barsVisible, insightTone, measuredSpans, placeLabels, rampLevels, stackLabels,
  visiblePointers, windowLevel,
} from '../insights/prepare';

// Ensemble insights on the canvas (spec section 7, prototype tab R8):
// - drawClear (behind the series): highlight wash, and in-plot bars when
//   less than 3 days are visible;
// - draw: the ribbon under the plot (in the x-axis gap, the "Expected" row)
//   and its labels, plus peak labels over the bars; with observed ramps
//   loaded, the Observed strip between the ribbon and its labels.
export const INSIGHTS_AXIS_GAP = 51; // ribbon (from 9 px down) + two label rows
export const INSIGHTS_TOP_PAD = PIN_ROW_H + 1; // room for the pin row (React overlay), no more

const RIBBON_H = RIBBON_ROW.h;
const OBSERVED_H = OBSERVED_STRIP.h;
const OBSERVED_GAP = OBSERVED_STRIP.y + OBSERVED_H - RIBBON_ROW.y - RIBBON_H; // the strip and the space above it
// The x-axis gap: room for the Observed strip only when it is drawn.
export const ribbonAxisGap = (observed) => INSIGHTS_AXIS_GAP + (observed ? OBSERVED_GAP : 0);
// The observed-ramps layer is loaded (observed=1) and not turned off.
export const observedShown = (st) => Array.isArray(st.prep.observed) && st.types?.observed !== false;
const LABEL_ROW = 15;
const LABEL_ROWS = 2; // what INSIGHTS_AXIS_GAP holds; more would hit the tick labels
const MIN_SHARE = 0.02;
const BAR_SHARE = 0.03;
const BAR_ALPHA = 0.55;
const FONT = "'Inter Variable', ui-sans-serif, system-ui, sans-serif";
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function slots(u, prep) {
  const { min, max } = u.scales.x;
  const out = [];
  for (let i = 0; i < prep.t.length; i += 1) {
    const t = prep.t[i];
    if (t + SLOT_SEC < min || t > max) continue;
    out.push({ i, x0: u.valToPos(t, 'x', true), x1: u.valToPos(t + SLOT_SEC, 'x', true) });
  }
  return out;
}

const shares = rampLevels;

// The whole layer's opacity (0 to 1): the chart fades it in and out.
const layerAlpha = (st) => (st.alpha == null ? 1 : st.alpha);

function drawHighlight(u, st) {
  if (!st.highlight) return;
  const { left, top, width, height } = u.bbox;
  const a = clamp(u.valToPos(Date.parse(st.highlight.start) / 1000, 'x', true), left, left + width);
  const b = clamp(u.valToPos(Date.parse(st.highlight.end) / 1000, 'x', true), left, left + width);
  if (b <= a) return;
  u.ctx.save();
  u.ctx.globalAlpha = 0.08 * layerAlpha(st);
  u.ctx.fillStyle = insightTone(st.highlight.kind, st.dark);
  u.ctx.fillRect(a, top, b - a, height);
  u.ctx.restore();
}

function drawBars(u, st) {
  const { min, max } = u.scales.x;
  if (min == null || !barsVisible(min, max)) return;
  const px = uPlot.pxRatio || 1;
  const { left, top, width, height } = u.bbox;
  const hMax = 0.4 * height; // a 50% share reaches 40% of the plot height
  const { ctx } = u;
  ctx.save();
  ctx.beginPath();
  ctx.rect(left, top, width, height);
  ctx.clip();
  ctx.globalAlpha = BAR_ALPHA * layerAlpha(st);
  slots(u, st.prep).forEach(({ i, x0, x1 }) => {
    const { down, up } = shares(st, i);
    const both = down >= BAR_SHARE && up >= BAR_SHARE;
    const gap = x1 - x0 >= 6 * px ? px : 0;
    const w = both ? (x1 - x0 - 2 * gap) / 2 : x1 - x0 - gap;
    [[down, 'drop', 0], [up, 'rise', 1]].forEach(([v, kind, side]) => {
      if (v < BAR_SHARE) return;
      const h = clamp(v / 0.5, 0, 1) * hMax;
      ctx.fillStyle = insightTone(kind, st.dark);
      ctx.fillRect(x0 + gap / 2 + (both && side ? w + gap : 0), top + height - h, Math.max(px, w), h);
    });
  });
  ctx.restore();
}

// The faint track under the ribbon and the Observed strip's marks.
const track = (dark) => (dark ? 'rgba(255,255,255,0.06)' : 'rgba(17,24,39,0.04)');

// A row's name in the y-axis margin, centred on the row: "Expected" (the
// ramps the selected method expects) over "Observed" (the ramps measured).
function caption(u, st, text, y) {
  const px = uPlot.pxRatio || 1;
  const { ctx } = u;
  ctx.save();
  ctx.globalAlpha = layerAlpha(st);
  ctx.font = `500 ${10 * px}px ${FONT}`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = insightTone('weak', st.dark);
  ctx.fillText(text, u.bbox.left - 4 * px, y);
  ctx.restore();
}

function drawRibbon(u, st) {
  const px = uPlot.pxRatio || 1;
  const { left, top, width, height } = u.bbox;
  const y = top + height + RIBBON_ROW.y * px;
  const H = RIBBON_H * px;
  const { ctx } = u;
  ctx.save();
  ctx.globalAlpha = layerAlpha(st);
  ctx.fillStyle = track(st.dark);
  ctx.fillRect(left, y, width, H);
  slots(u, st.prep).forEach(({ i, x0, x1 }) => {
    const { down, up } = shares(st, i);
    const both = down >= MIN_SHARE && up >= MIN_SHARE;
    const a = Math.max(left, x0);
    const w = Math.min(left + width, x1) - a;
    if (w <= 0) return;
    [[down, 'drop', 0], [up, 'rise', 1]].forEach(([v, kind, side]) => {
      if (v < MIN_SHARE) return;
      ctx.globalAlpha = clamp(v / 0.5, 0.2, 1) * layerAlpha(st);
      ctx.fillStyle = insightTone(kind, st.dark);
      ctx.fillRect(a, both && side ? y + H / 2 : y, w, both ? H / 2 : H);
    });
  });
  ctx.restore();
  caption(u, st, 'Expected', y + H / 2);
}

// Measured ramps as solid blocks in the drop and rise colours on a thin strip
// under the ribbon, over a faint track where there are measurements (up to
// `observed_until`, without the gaps), captioned "Observed". A block
// is clipped to the plot first, then kept at least 4 px wide, so a 1 h ramp
// stays visible over a month.
function drawObserved(u, st) {
  if (!observedShown(st)) return;
  const px = uPlot.pxRatio || 1;
  const { left, top, width, height } = u.bbox;
  const right = left + width;
  const y = top + height + OBSERVED_STRIP.y * px;
  const H = OBSERVED_H * px;
  const xOf = (sec) => u.valToPos(sec, 'x', true);
  const { ctx } = u;
  caption(u, st, 'Observed', y + H / 2);
  ctx.save();
  ctx.globalAlpha = layerAlpha(st);
  ctx.fillStyle = track(st.dark);
  measuredSpans(st.prep, u.scales.x.min, u.scales.x.max).forEach(([a, b]) => {
    const x0 = Math.max(left, xOf(a));
    const x1 = Math.min(right, xOf(b));
    if (x1 > x0) ctx.fillRect(x0, y, x1 - x0, H);
  });
  st.prep.observed.forEach((o) => {
    const x0 = Math.max(left, xOf(Date.parse(o.start) / 1000));
    const x1 = Math.min(right, xOf(Date.parse(o.end) / 1000));
    if (x1 <= left || x0 >= right) return;
    const w = Math.max(x1 - x0, 4 * px);
    ctx.fillStyle = insightTone(o.type, st.dark);
    ctx.fillRect(clamp(x0 - (w - (x1 - x0)) / 2, left, right - w), y, w, H);
  });
  ctx.restore();
}

function drawLabels(u, st) {
  const px = uPlot.pxRatio || 1;
  const { min, max } = u.scales.x;
  const { left, top, width, height } = u.bbox;
  const zoomed = barsVisible(min, max);
  const ramps = visiblePointers(st.prep.pointers, st)
    .filter((p) => p.type === 'ramp_down' || p.type === 'ramp_up');
  const { ctx } = u;
  ctx.save();
  ctx.globalAlpha = layerAlpha(st);
  ctx.font = `600 ${11 * px}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  // Labels only for marks inside the plot; clamping keeps them from the edges.
  const inView = (x) => x >= left && x <= left + width;
  const ribbonItems = ramps.map((p) => {
    const text = ribbonLabel(p, zoomed, st.prep.params);
    const w = ctx.measureText(text).width;
    const at = u.valToPos(Date.parse(p.pin) / 1000, 'x', true);
    return { x: clamp(at, left + w / 2, left + width - w / 2), at, w, text, p };
  }).filter((it) => inView(it.at));
  const below = RIBBON_ROW.y + RIBBON_H + 14 + (observedShown(st) ? OBSERVED_GAP : 0);
  placeLabels(ribbonItems, 8 * px).filter((it) => it.row < LABEL_ROWS).forEach((it) => {
    ctx.fillStyle = insightTone(it.p.type, st.dark, true);
    ctx.fillText(it.text, it.x, top + height + (below + it.row * LABEL_ROW) * px);
  });
  if (zoomed) {
    const peakItems = ramps.map((p) => {
      const key = p.type === 'ramp_up' ? 'up' : 'down';
      const series = st.prep[key];
      const a = Date.parse(p.start) / 1000;
      const b = Date.parse(p.end) / 1000;
      let best = -1;
      let at = Date.parse(p.pin) / 1000;
      let peakX;
      if (rampMethodOf(p, st.prep.params) === 'forecast') {
        // A window is flat: label its middle (the pin), above the bar drawn there.
        const i = st.prep.indexOf.get(at);
        best = i == null ? windowLevel(p) : Math.max(windowLevel(p), shares(st, i)[key]);
        peakX = u.valToPos(at, 'x', true);
      } else {
        st.prep.t.forEach((t, i) => { if (t >= a && t < b && (series[i] || 0) > best) { best = series[i] || 0; at = t; } });
        peakX = u.valToPos(at + SLOT_SEC / 2, 'x', true);
      }
      const text = peakLabel(p, st.prep.params);
      const w = ctx.measureText(text).width;
      const barTop = top + height - clamp(best / 0.5, 0, 1) * 0.4 * height;
      return { x: clamp(peakX, left + w / 2, left + width - w / 2), peakX, w, text, p, y: barTop - 8 * px };
    }).filter((it) => inView(it.peakX));
    stackLabels(peakItems, 8 * px, LABEL_ROW * px).forEach((it) => {
      ctx.fillStyle = insightTone(it.p.type, st.dark, true);
      ctx.fillText(it.text, it.x, it.y);
    });
  }
  ctx.restore();
}

export default function insightsPlugin({ getState }) {
  const current = () => {
    const st = getState();
    return st && st.prep ? { types: {}, ...st } : null;
  };
  return {
    hooks: {
      drawClear: [(u) => {
        const st = current();
        if (!st) return;
        drawHighlight(u, st);
        drawBars(u, st);
      }],
      draw: [(u) => {
        const st = current();
        // Pointers without insight rows (revisions) have pins only: no ribbon.
        if (!st || !st.prep.t.length) return;
        drawRibbon(u, st);
        drawObserved(u, st);
        drawLabels(u, st);
      }],
    },
  };
}
